import { METRONOME_LOOKAHEAD_MS } from '@/constants';
import type { Note } from '@/types';
import { computeMsPerPixel } from '@/utils';
import { useInjectGameEngine } from './useGameEngine';
import { useHorizontalDrag } from './useHorizontalDrag';

type TimeScrubDragOptions = {
  /** Notes backing the rendered sheet, used to derive the px→ms rate. */
  notes: () => Note[];
  /** Upper bound for the scrub, in song-time ms. */
  durationMs: () => number;
  /** CSS scale applied to the sheet container; note coordinates are pre-transform. */
  scale?: () => number;
  /** Gate the gesture (sheet loaded, seeking permitted, …). */
  enabled?: () => boolean;
  /** Runs once the gesture is accepted and playback has been stopped. */
  onScrubStart?: () => void;
  /** Runs on every scrub step. `wasPlaying` is latched before playback stopped. */
  onScrub?: (timeMs: number, wasPlaying: boolean) => void;
  /** Runs on release when the press was a tap, not a scrub — see `useHorizontalDrag`. */
  onTap?: () => void;
};

/**
 * Drag the sheet music horizontally to scrub the game clock. Drag right rewinds.
 * Playback stops when the drag is accepted, never at the press — see
 * `ACTIVATION_TRAVEL_PX`.
 */
export function useTimeScrubDrag({
  notes,
  durationMs,
  scale,
  enabled,
  onScrubStart,
  onScrub,
  onTap,
}: TimeScrubDragOptions) {
  const gameEngine = useInjectGameEngine();
  let anchorTimeMs = 0;
  let msPerPx = 0;
  let wasPlaying = false;

  return useHorizontalDrag({
    onStart: () => {
      if (enabled?.() === false) return false;
      const rate = computeMsPerPixel(notes(), scale?.() ?? 1);
      if (rate === null) return false;

      msPerPx = rate;
      wasPlaying = gameEngine.isPlaying.value;
      if (wasPlaying) gameEngine.stopPlayback();
      // seekTo(t) parks the clock at `t - METRONOME_LOOKAHEAD_MS`, so anchor on the
      // seek target rather than the raw clock — otherwise the sheet would jump back
      // one lookahead at the start of every drag.
      anchorTimeMs = gameEngine.currentTime.value + METRONOME_LOOKAHEAD_MS;
      onScrubStart?.();
    },
    onMove: (deltaX) => {
      const timeMs = Math.min(Math.max(0, anchorTimeMs - deltaX * msPerPx), durationMs());
      gameEngine.seekTo(timeMs);
      onScrub?.(timeMs, wasPlaying);
    },
    onTap,
  });
}
